import { type EventFactory } from '@thorne/hightouch-clickstream'
import { useUserStore } from "~/stores/userStore";

/**
 * Registers client-side Hightouch page-event handling for login transitions.
 *
 * When the backend sets the `loginEvent=true` cookie, this plugin waits until
 * the app has mounted or the route changes, refreshes the current user, sends a
 * Hightouch identify event with the customer's profile data, and then clears the
 * cookie so the event is not emitted again.
 */
export default defineNuxtPlugin({
    name: 'hightouch-clickstream-page-events',
    dependsOn: ['hightouch-clickstream'],
    setup(nuxtApp) {
        const router = useRouter()
        let isProcessingLoginEvent = false

        const checkUserLoginTransition = async () => {
            const hightouchEvents = nuxtApp.$hightouchEvents as EventFactory | null | undefined

            if (!hightouchEvents || isProcessingLoginEvent) return
            if (!hasCookie('loginEvent', 'true')) return

            const userStore = useUserStore()

            isProcessingLoginEvent = true

            try {
                await userStore.fetch()

                const user = userStore.data
                if(!user?.customerId) return

                hightouchEvents.identify(String(user.customerId), {
                    email: user.username,
                    name: user.name
                }, {
                    callback: (error) => {
                        if (error) {
                            console.warn('Failed to track Hightouch login event', error)
                        }
                    }
                })

                clearCookie('loginEvent')
            } catch (error) {
                console.warn('Failed to track Hightouch login event', error)
            } finally {
                isProcessingLoginEvent = false
            }
        }

        nuxtApp.hook('app:mounted', () => {
            checkUserLoginTransition()
        })

        router.afterEach(() => {
            checkUserLoginTransition()
        })
    }
})

/**
 * Checks whether the browser currently has an exact cookie name/value pair.
 *
 * This is used for simple server-set event flags, such as `loginEvent=true`,
 * where the frontend only needs to know whether the event should be processed.
 */
function hasCookie(name: string, value: string): boolean {
    return document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .some(cookie => cookie === `${name}=${value}`)
}

/**
 * Expires a cookie at the root path so it cannot trigger future page loads.
 */
function clearCookie(name: string): void {
    document.cookie = `${name}=; Max-Age=0; path=/`
}
